import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, takeUntil } from 'rxjs/operators';
import { ShellInitHelper } from "../../../app/shell/services/ShellInitHelper";
import { LogCat, LogManager } from '../../../common/log';
import { LookupsService } from '../../lookups/services/lookups.service';
import { UserContextService } from "../../user.context/services/user.context.service";
import { IFavoriteLocation, ILocation } from "../interfaces/favorite.locations";
import { OrganizationStructure } from '../organization.structure';
import { Node, OrganizationStructureTree } from '../organization.structure.tree';


/**
 * ***************************************************************
 *  Written by: Dana Moshe
 * ***************************************************************
 */


@Injectable({ providedIn: 'root' })
  export class OrganizationStructureService implements OnDestroy {//extends LoadData

	public cachedOrganizationStructureTree: OrganizationStructureTree;
	public subjectSelectedOrgStructure: BehaviorSubject<OrganizationStructure.OrgStructure>;
	private subjectDestroyed$: Subject<void> = new Subject<void>();


	constructor(private lookupsService: LookupsService,
				private shellIniter: ShellInitHelper,
				private userContextService: UserContextService) {
		this.subjectSelectedOrgStructure = new BehaviorSubject(null);
		// tslint:disable-next-line:rxjs-subscribe-requires-destroy
		this.shellIniter.OrganizationSturcture().pipe(takeUntil(this.subjectDestroyed$)).subscribe(x => {
			this.cachedOrganizationStructureTree = new OrganizationStructureTree(x.orgStructure);
		});
	}

	ngOnDestroy() {
		this.subjectDestroyed$.next();
		this.subjectDestroyed$.complete();
	}


	/**
	 * Load all the data for organization structure service.
	 * @returns Observable that will receive the events of
	 * loading of the organization structure tree.
	 */
	public loadDataObservable():Observable<OrganizationStructureTree> {

		LogManager.debug(LogCat.LookupsCategories.OrganizationStructure, "The function loadDataPromise start.");

		return this.shellIniter.OrganizationSturcture().map(
			(res:OrganizationStructure.OrganizationStructureDS)=>new OrganizationStructureTree(res.orgStructure)
		);
	}

	/**
	 *  Get the org-structure tree.
	 *  @returns the tree that represent the organization structure.
	 */
	public getOrganizationStructureTree():OrganizationStructureTree {

		LogManager.debug(LogCat.LookupsCategories.OrganizationStructure, "The function getOrganizationStructureTree called.");
		return this.cachedOrganizationStructureTree;
	}

	/**
	 * Get a sub-tree by organization structure key.
	 * @param orgStructureKey key of orgStructure to search.
	 * @returns a node that is a sub-tree of the root node.
	 * If the key not found return undefined.
	 */
	public getOrganizationStructureByKey(orgStructureKey:number):Node {

		LogManager.debug(LogCat.LookupsCategories.OrganizationStructure, "The function getOrganizationStructureByKey called.");

		return OrganizationStructureTree.getOrganizationStructureByKey(orgStructureKey, this.cachedOrganizationStructureTree.root);
	}

	/**
	 *
	 * @param {number} orgStructureKey
	 * @returns {Node[]}
	 */
	public getPathToOrgStructureKey(orgStructureKey: number): Node[] {
		let resultArray = [];
		let root: Node = this.getOrganizationStructureTree().root;

		if (this.getPathToOrgStructureKeyStep(orgStructureKey, root, resultArray)) {
			resultArray.push(root);
		}

		return resultArray;
	}

	public getCodePathToOrgStructureKey(orgStructureKey: number): string {
		let result = '';
		let path = this.getPathToOrgStructureKey(orgStructureKey).reverse();

		path.forEach((p, index) => {
			result += p.data.code;
			if (index < path.length - 1) {
				result += '.';
			}
		});

		return result;
	}
	public getParentPathToOrgStructureKey(orgStructureKey: number, column: string, showFullPath: boolean = false): string[] {
		let result = [];
		let path = this.getPathToOrgStructureKey(orgStructureKey).reverse();
		path.forEach((p, index) => {
			if (!showFullPath) {
				if (index === path.length - 2) {
					result.push(p.data[column]);
				}
			} else {
				if (index < path.length - 1) {
					result.push(p.data[column]);
				}
			}
		});
		return result;
	}

	/**
	 * Set the org-structure that selected.
	 * @param orgStructure the org-structure that selected.
	 */
	public setSelectedOrgStructure(orgStructure:OrganizationStructure.OrgStructure):void {
		this.subjectSelectedOrgStructure.next(orgStructure);
	}

	// /**
	//  * Gets the list of favorite locations.
	//  * @returns {IFavoriteLocation[]}
	//  */
	public getCurrFavoriteLocations(): IFavoriteLocation[] {
		let locations: ILocation[] = this.userContextService.getCachedUserContext().userPreferences.favoriteLocations;
		if (locations) {
			return locations.map(location => {
				let orgStructureNode = this.getOrganizationStructureByKey(location.orgStructureKey);
				if (orgStructureNode) {
					return {
						key: location.orgStructureKey,
						description: orgStructureNode.data.description
					};
				}
			}).filter(x => x);
		} else {
			return [];
		}
	}

	/**
	 * Gets the list of favorite locations.
	 * @returns {IFavoriteLocation[]}
	 */
	public getFavoriteLocations(): Observable<IFavoriteLocation[]> {
		return this.userContextService.getUserContext().pipe(map(x => x.userPreferences.favoriteLocations),map(location => {
			if (location) {
				return location.map(x => {
					let orgStructureNode = this.getOrganizationStructureByKey(x.orgStructureKey);
					if (orgStructureNode) {
						return {
							key: x.orgStructureKey,
							description: orgStructureNode.data.description
						};
					}
				}).filter(x => x);
			} else {
				return [];
			}
		}),);
	}

	/**
	 *
	 * @param {number} orgStructureKey
	 * @param {Node} root
	 * @param {Node[]} nodeArray
	 * @returns {boolean}
	 */
	public getPathToOrgStructureKeyStep(orgStructureKey: number, root: Node, nodeArray: Node[] = []): boolean {
		if (root.data.orgStructureKey === orgStructureKey) {
			return true;
		}

		if (root.items) {
			return root.items.some((node: Node) => {
				if (this.getPathToOrgStructureKeyStep(orgStructureKey, node, nodeArray)) {
					nodeArray.push(node);
					return true;
				}
				return false;
			});
		} else {
			return false;
		}
	}

	/**
	 *
	 * @returns {string}
	 */
	public getCurrentLocation(): string {
		let currentLocationKey = this.userContextService.getCachedUserContext().currentOrganization;
		let currentLocationNode = this.getOrganizationStructureByKey(currentLocationKey);
		return currentLocationNode ? currentLocationNode.data.description : this.getSiteName();
	}

	public getLocation(orgStructureKey: number): string {
		let currentLocationNode = this.getOrganizationStructureByKey(orgStructureKey);
		return currentLocationNode ? currentLocationNode.data.description : this.getSiteName();
	}

	/**
	 *
	 * @returns {Observable<Node>}
	 */
	public getCurrentLocationObservable(): Observable<Node> {
		return this.userContextService.getUserContext().pipe(map(x => {
			return this.getOrganizationStructureByKey(x.currentOrganization) || this.getOrganizationStructureTree().root;
		}));
	}

	/**
	 *
	 * @returns {string}
	 */
	public getSiteName(): string {
		return this.getOrganizationStructureTree().root.data.description;
	}

	/**
	 *
	 * @param {number} orgStructureKey
	 * @returns {Node[]}
	 */
	public getAllChildrenLocations(orgStructureKey: number): number[] {
		let res: number[] = [];
		let currNode = this.getOrganizationStructureByKey(orgStructureKey);
		if (currNode && currNode.items) {
			currNode.items.forEach(child => {
				this.getChldrenLocations(res, child);
			});
		}
		return res;
	}

	/**
	 *
	 * @param {number[]} locations
	 * @param {Node} node
	 */
	public getChldrenLocations(locations: number[], node: Node) {
		locations.push(node.data.orgStructureKey);
		if (node.items) {
			node.items.forEach(item => {
				this.getChldrenLocations(locations, item);
			});
		}
	}

	/**
	 * Searches for the node that allows orders and is used as facility. Required for creating new order.
	 * If current node doesn't match the requirements, the search continues for parent nodes until we reach the root node.
	 * @param orgStructureKey
	 */
	public getNearestFacilityAllowsOrderNodeKey(orgStructureKey: number): number {
		let currNode = this.getOrganizationStructureByKey(orgStructureKey);
		if (currNode && currNode.data) {
			if (!currNode.data.parentOrgStructureKey || (currNode.data.allowOrders === 'Y' && currNode.data.facilityFlag === 'Y')) {
				return orgStructureKey;
			} else if (currNode.data.parentOrgStructureKey) {
				return this.getNearestFacilityAllowsOrderNodeKey(currNode.data.parentOrgStructureKey);
			}
		}
		return this.cachedOrganizationStructureTree.root.data.orgStructureKey;
	}

	/**
	 * Searches for the node that is used as facility.
	 * If current node doesn't match the requirements, the search continues for parent nodes until it reaches the root node.
	 * @param orgStructureKey
	 */
	public getNearestFacility(orgStructureKey: number): number {
		let currNode = this.getOrganizationStructureByKey(orgStructureKey);
		if (currNode && currNode.data) {
			if (currNode.data.facilityFlag === 'Y' || !currNode.data.parentOrgStructureKey) {
				return orgStructureKey;
			} else if (currNode.data.parentOrgStructureKey) {
				return this.getNearestFacility(currNode.data.parentOrgStructureKey);
			}
		}
		return this.cachedOrganizationStructureTree.root.data.orgStructureKey;
	}
}
